import { Box, Grid, Button, CircularProgress, Divider } from '@mui/material';
import React, { useState, useEffect } from 'react';
import {
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  ScrollableTabs,
  WsColors,
} from 'wisestep-base';

import { useNotifications } from '../../../hooks/useNotifications';

import { NotificationCard } from './NotificationCard';
import { TabPanel } from './TabPanel/TabPanel';

export const NotificationList = () => {
  const [tabValue, setTabValue] = useState(0);
  const { notifications, loading, markAsRead, markAllAsRead, refresh, totalElements } =
    useNotifications();

  const handleTabChange = async (index: number) => {
    setTabValue(index);
    // Fetch appropriate notifications based on tab
    await refresh({ isRead: index === 1 });
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  // Fetch initial notifications
  useEffect(() => {
    refresh({ isRead: tabValue === 1 });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // No need to filter since we're fetching the correct notifications based on tab
  const tabs = [
    { tabName: 'Unread', tabCount: tabValue === 0 ? totalElements : 0 },
    { tabName: 'Read', tabCount: tabValue === 1 ? totalElements : 0 },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        overflowY: 'auto',
        flexDirection: 'column',
        maxHeight: '80vh',
        pb: 5,
      }}
    >
      <Grid container alignItems="center" sx={{ px: 2, py: 1, flexShrink: 0 }}>
        <Grid item xs>
          <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
            Notifications
          </WsTypography>
        </Grid>
        {tabValue === 0 && totalElements > 0 && (
          <Grid item>
            <Button color="primary" onClick={handleMarkAllAsRead}>
              Mark all as read
            </Button>
          </Grid>
        )}
      </Grid>

      <ScrollableTabs
        scrollList={tabs}
        getTabIndex={handleTabChange}
        defaultValue={tabValue}
        divider={true}
        scrollButtons="auto"
      />

      <TabPanel value={tabValue} index={0}>
        <Box>
          {loading ? (
            <Grid container direction="column" alignItems="center" spacing={1}>
              <Grid item>
                <CircularProgress size={24} />
              </Grid>
              <Grid item>
                <WsTypography variant={WsTypoVariants.BODY1}>Loading notifications...</WsTypography>
              </Grid>
            </Grid>
          ) : notifications.length === 0 ? (
            <Grid container direction="column" alignItems="center" spacing={1}>
              <Grid item>
                <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.PRIMARY}>
                  {tabValue === 0 ? 'All caught up!' : 'No read notifications'}
                </WsTypography>
              </Grid>
              <Grid item>
                <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.SECONDARY}>
                  {tabValue === 0
                    ? "You've read all the notifications from the last 30 days."
                    : "Notifications you've read will appear here."}
                </WsTypography>
              </Grid>
            </Grid>
          ) : (
            <Grid container>
              {notifications.map((notification, index) => (
                <Grid item xs={12} key={notification.id}>
                  <NotificationCard notification={notification} onMarkAsRead={markAsRead} />
                  {index !== notifications.length - 1 && (
                    <Divider sx={{ height: '2px', bgcolor: WsColors.TertiaryShades100 }} />
                  )}
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box
          sx={{
            p: notifications.length === 0 ? 4 : 0,
            textAlign: notifications.length === 0 ? 'center' : 'left',
          }}
        >
          {notifications.length === 0 ? (
            <Grid container direction="column" alignItems="center" spacing={1}>
              <Grid item>
                <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.PRIMARY}>
                  No read notifications
                </WsTypography>
              </Grid>
              <Grid item>
                <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.SECONDARY}>
                  Notifications you&apos;ve read will appear here.
                </WsTypography>
              </Grid>
            </Grid>
          ) : (
            <Grid container>
              {notifications.map((notification, index) => (
                <Grid item xs={12} key={notification.id}>
                  <NotificationCard notification={notification} onMarkAsRead={markAsRead} />
                  {index !== notifications.length - 1 && (
                    <Divider sx={{ height: '2px', bgcolor: WsColors.White }} />
                  )}
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </TabPanel>
    </Box>
  );
};
